import { useListBillingPlans, useSetupIntentClientSecret } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ROUTES } from "@/constants/routes";
import { useInvoicePreview } from "@/hooks/useInvoicePreview";
import * as analytics from "@/utils/analytics";
import { confirmSetupReturnUrl } from "@/utils/confirmSetup";
import { formatCurrency, formatCurrencyFromCents } from "@/utils/currency";
import type { BillingInterval } from "@cohesive/api-client";
import {
  AddressElement,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle } from "lucide-react";

import React, { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

export default function SetupForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { data: setupIntentClientSecret } = useSetupIntentClientSecret();
  const { data: userInfo } = useUserInfo();

  const [errorMessage, setErrorMessage] = useState();

  // Check if this is a payment method update
  const isPaymentMethodUpdate =
    searchParams.get("mode") === "payment_method_update";

  // Check if we're in the onboarding flow
  const isOnboardingFlow = location.pathname.startsWith(ROUTES.ONBOARDING.ROOT);

  // Get plan details from query params (only for new subscriptions)
  const planName = searchParams.get("plan");
  const billingCycle = searchParams.get("billing_cycle") as BillingInterval;
  const userCount = parseInt(searchParams.get("user_count") || "1");
  const redirectTo = searchParams.get("redirect_to");
  // Card is being collected for a payment-method-required trial; the trial
  // must not end when the card attaches.
  const keepTrial = searchParams.get("keep_trial") === "1";

  const handleError = (error: any) => {
    setErrorMessage(error.message);
  };

  const handleSubmit = useMutation({
    mutationFn: async (event: React.FormEvent<HTMLFormElement>) => {
      // We don't want to let default form submission happen here,
      // which would refresh the page.
      event.preventDefault();

      // Track checkout submission
      analytics.trackBillingCheckoutSubmitted({
        planName: planName || undefined,
        billingInterval: billingCycle || undefined,
        seatCount: userCount || undefined,
        isPaymentMethodUpdate,
        organization_id: userInfo?.organization?.id,
      });

      if (!stripe) {
        // Stripe.js hasn't yet loaded.
        // Make sure to disable form submission until Stripe.js has loaded.
        return;
      }

      if (!elements) {
        return;
      }

      // Trigger form validation and wallet collection
      const { error: submitError } = await elements.submit();
      if (submitError) {
        handleError(submitError);
        return;
      }

      // const res = await setupIntent.mutateAsync();
      // const clientSecret = res.data.client_secret;

      // Use onboarding confirm setup route if in onboarding flow
      const confirmSetupRoute = isOnboardingFlow
        ? ROUTES.ONBOARDING.CONFIRM_SETUP
        : ROUTES.BILLING.CONFIRM_SETUP;

      // Confirm the SetupIntent using the details collected by the Payment Element
      const { error } = await stripe.confirmSetup({
        elements,
        clientSecret: setupIntentClientSecret?.data.client_secret!,
        confirmParams: {
          return_url: confirmSetupReturnUrl({
            intent: {
              planName,
              billingCycle,
              userCount,
              isPaymentMethodUpdate,
              keepTrial,
            },
            redirectTo,
            confirmSetupRoute,
          }),
        },
        redirect: "always",
      });

      if (error) {
        throw new Error(error.message);
      }
    },

    onError: (error) => {
      handleError(error);
    },
  });

  return (
    <form onSubmit={handleSubmit.mutateAsync}>
      {errorMessage && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <div className="mb-2">
        <PaymentElement
          options={{
            layout: "tabs",
            fields: {
              billingDetails: {
                name: "never",
              },
            },
          }}
        />
      </div>
      <div className="mb-8">
        <AddressElement
          options={{
            mode: "billing",
          }}
        />
      </div>
      <Button
        data-testid="submit-button"
        className="w-full"
        size="xl"
        type="submit"
        disabled={!stripe}
        isLoading={handleSubmit.isPending}
      >
        Submit
      </Button>
    </form>
  );
}

function SelectedPlanCard({ isTrial = false }: { isTrial?: boolean }) {
  const [searchParams] = useSearchParams();
  const { data: billingPlans } = useListBillingPlans();

  const planName = searchParams.get("plan");
  const billingCycle = searchParams.get("billing_cycle") as BillingInterval;
  const userCount = parseInt(searchParams.get("user_count") || "1");

  // Find the selected plan and determine available intervals
  const planData = useMemo(() => {
    if (!billingPlans?.data.billing_plans || !planName) return null;

    const allPlansForName = billingPlans.data.billing_plans.filter(
      (plan) => plan.name === planName,
    );

    const selectedPlan = allPlansForName.find(
      (plan) => plan.billing_interval === billingCycle,
    );

    if (!selectedPlan) return null;

    const availableIntervals = allPlansForName.map((p) => p.billing_interval);

    return {
      selectedPlan,
      availableIntervals,
      hasMonthlyOption: availableIntervals.includes("month"),
      hasYearlyOption: availableIntervals.includes("year"),
    };
  }, [billingPlans, planName, billingCycle]);

  // Use the invoice preview hook for dynamic pricing
  const {
    totalPrice,
    discount: savings,
    isLoading,
  } = useInvoicePreview(
    {
      planName: planName || "",
      billingInterval: billingCycle,
      seatCount: userCount,
      enabled: !!planData?.selectedPlan && !!planName,
    },
    {
      debounceMs: 300, // Shorter debounce for checkout page
      autoPreview: true,
    },
  );

  if (!planData?.selectedPlan || !planName) return null;

  const { selectedPlan } = planData;

  // During a trial nothing is due today, so show the recurring seat total
  // (what converts when the trial ends) instead of the $0 invoice preview.
  const recurringTotalCents = selectedPlan.seat_price * userCount;

  return (
    <Card
      data-testid="selected-plan-card"
      className="mb-6 border-lc-active bg-lc-active/10 dark:bg-spectrum-green-dark/30/20"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg text-foreground">
            {isTrial ? "Your Trial Plan" : "Your Selected Plan"}
          </CardTitle>
          {isLoading ? (
            <Skeleton className="h-6 w-20" />
          ) : (
            savings > 0 &&
            planData.hasMonthlyOption &&
            billingCycle === "year" && (
              <div
                data-testid="savings-badge"
                className="bg-spectrum-green text-white text-xs font-medium px-2 py-1 rounded-md"
              >
                Save {formatCurrency(savings)}
              </div>
            )
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex justify-between items-start">
          <div>
            <h4
              data-testid="plan-name"
              className="font-semibold text-lg text-foreground"
            >
              {selectedPlan.display_name}
            </h4>
            <p
              data-testid="billing-details"
              className="text-sm text-muted-foreground"
            >
              {userCount} user{userCount > 1 ? "s" : ""} • Billed{" "}
              {billingCycle === "year" ? "annually" : "monthly"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {selectedPlan.included_credits_per_seat} credits included per seat
            </p>
            {isLoading ? (
              <Skeleton className="h-4 w-48 mt-1" />
            ) : (
              savings > 0 &&
              planData.hasMonthlyOption &&
              billingCycle === "year" && (
                <p className="text-xs text-muted-foreground mt-1 font-medium">
                  You're saving {formatCurrency(savings)} compared to monthly
                  billing
                </p>
              )
            )}
          </div>
          <div className="text-right">
            {isLoading ? (
              <div className="space-y-1">
                <Skeleton className="h-8 w-20 ml-auto" />
                <Skeleton className="h-4 w-24 ml-auto" />
              </div>
            ) : (
              <>
                <div
                  data-testid="total-price"
                  className="text-2xl font-bold text-foreground"
                >
                  {isTrial
                    ? formatCurrencyFromCents(recurringTotalCents, {
                        hideCentsForWholeNumbers: true,
                      })
                    : formatCurrency(totalPrice, {
                        hideCentsForWholeNumbers: true,
                      })}
                </div>
                <div className="text-sm text-muted-foreground">
                  {formatCurrencyFromCents(selectedPlan.seat_price, {
                    hideCentsForWholeNumbers: true,
                  })}
                  /seat per {billingCycle === "year" ? "year" : "month"}
                </div>
                {isTrial && (
                  <div className="text-xs text-muted-foreground mt-1">
                    Billed when your trial ends
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export const CheckoutPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { data: userInfo } = useUserInfo();
  const isPaymentMethodUpdate =
    searchParams.get("mode") === "payment_method_update";
  // keep_trial means the card attaches without ending the trial; its absence on
  // a trialing subscription means adding a card ends the trial and bills now.
  const keepTrial = searchParams.get("keep_trial") === "1";

  const subscription = userInfo?.subscription;

  // keep_trial is only ever set by the trial card-gate (the useRequireSubscription
  // redirect and the add-payment hook, both trialing-only), so it authoritatively
  // marks a begin-trial checkout without waiting for /me — avoids a flash of
  // purchase copy while the subscription loads.
  const isTrialPaymentMethodSetup = !isPaymentMethodUpdate && keepTrial;
  // Ending the trial now: the card is charged immediately to start billing.
  const isTrialEndingSetup =
    !isPaymentMethodUpdate &&
    !keepTrial &&
    subscription?.status === "trialing" &&
    !subscription.default_payment_method_id;

  const trialEndsAt =
    isTrialPaymentMethodSetup && subscription?.current_period_end
      ? new Date(subscription.current_period_end).toLocaleDateString(
          undefined,
          { month: "long", day: "numeric", year: "numeric" },
        )
      : null;

  const trialChargeAmount =
    isTrialEndingSetup && subscription
      ? formatCurrencyFromCents(
          subscription.seat_price * subscription.seat_count,
          { hideCentsForWholeNumbers: true },
        )
      : null;

  const title = isPaymentMethodUpdate
    ? "Update Payment Method"
    : isTrialPaymentMethodSetup
      ? "Add a payment method"
      : isTrialEndingSetup
        ? "Add a payment method to end your trial"
        : "Complete Your Purchase";

  const subtitle = isPaymentMethodUpdate
    ? "Update your default payment method securely"
    : isTrialPaymentMethodSetup
      ? `You won't be charged until your trial ends${trialEndsAt ? ` on ${trialEndsAt}` : ""}. Your payment information is encrypted and secure.`
      : isTrialEndingSetup
        ? `Your free trial will end and your card will be charged${trialChargeAmount ? ` ${trialChargeAmount}` : ""} to start your subscription. Your payment information is encrypted and secure.`
        : "Your payment information is encrypted and secure";

  return (
    <>
      <title>{title}</title>
      <div className="container max-w-3xl mx-auto py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2">{title}</h1>
          <p className="text-muted-foreground">{subtitle}</p>
        </div>

        {/* Plan summary for every setup except a bare payment-method update.
            The trial gate shows the plan being trialed (recurring cost, billed
            after the trial) rather than a due-today total. */}
        {!isPaymentMethodUpdate && (
          <SelectedPlanCard isTrial={isTrialPaymentMethodSetup} />
        )}

        <SetupForm />

        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            🔒 Secure payment powered by Stripe.
          </p>
        </div>
      </div>
    </>
  );
};
